import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  IconExpand,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  ResponseError,
} from '@ui/src';
import { useAppSelector } from '@store';
import { PurposeCodeSettings } from '../settings/types';
import PurposeCodeItem from '../../components/settings/purpose-codes/purpose-codes';
import {
  useLazyGetPurposeCodesSettingsQuery,
  useCreatePurposeCodeSettingMutation,
  useUpdatePurposeCodeSettingMutation,
  useDeletePurposeCodeSettingMutation,
} from '../settings/store/services/purpose-codes';

import cn from '../settings/styles.module.css';

const PurposeCodes = () => {
  const [purposeCodesSettings, setPurposeCodesSettings] = useState<
    PurposeCodeSettings[]
  >([
    {
      id: 'purposeCode-0',
      purposeCodeType: '',
      paymentCategoryId: '',
      bankCountry: '',
      currenciesIds: [],
      required: false,
    },
  ]);
  const [selectedPurposeCodeSettingId, setSelectedPurposeCodeSettingId] =
    useState('');
  const [creationErrors, setCreationErrors] = useState<
    { id: string; error: ResponseError }[]
  >([]);
  const [updateErrors, setUpdateErrors] = useState<
    { id: string; error: ResponseError }[]
  >([]);

  const { paymentTypes } = useAppSelector(
    (state) => state.settings.paymentTypes,
  );
  const { countriesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );
  const { purposeCodesSettings: purposeCodesSettingsList } = useAppSelector(
    (state) => state.purposeCodesSettings,
  );

  const [getPurposeCodesSettings, { isLoading, isFetching }] =
    useLazyGetPurposeCodesSettingsQuery();
  const [createPurposeCodeSettings, { isLoading: createInProgress }] =
    useCreatePurposeCodeSettingMutation();
  const [updatePurposeCodeSettings, { isLoading: updateInProgress }] =
    useUpdatePurposeCodeSettingMutation();
  const [
    deletePurposeCodeSettings,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeletePurposeCodeSettingMutation();

  const onDeletePurposeCodeSettings = async (id: string) => {
    if (id.includes('purposeCode')) {
      setPurposeCodesSettings(
        purposeCodesSettings?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedPurposeCodeSettingId(id);

      await deletePurposeCodeSettings(id);
    }
  };

  const onSubmit = async () => {
    setCreationErrors([]);
    setUpdateErrors([]);

    let nextPurposeCodeSettings = ([] as PurposeCodeSettings[]).concat(
      purposeCodesSettings,
    );

    let updateIsAvailable = true;

    const purposeCodeSettingsForCreate = purposeCodesSettings?.filter((item) =>
      item.id.includes('purposeCode'),
    );
    const purposeCodeSettingsForUpdate: PurposeCodeSettings[] = [];

    purposeCodesSettings?.forEach((item) => {
      if (!item.id.includes('purposeCode')) {
        const currentDefaultPurposeCodeSetting = purposeCodesSettingsList?.find(
          (purposeCodeSetting) => item.id === purposeCodeSetting.id,
        );

        if (
          currentDefaultPurposeCodeSetting &&
          !isEqual(
            {
              id: item.id,
              purposeCodeType: item.purposeCodeType,
              paymentCategoryId: item.paymentCategoryId,
              bankCountry: item.bankCountry,
              currenciesIds: item.currenciesIds,
              required: item.required,
            },
            {
              id: currentDefaultPurposeCodeSetting.id,
              purposeCodeType: currentDefaultPurposeCodeSetting.purposeCodeType,
              paymentCategoryId:
                currentDefaultPurposeCodeSetting.paymentCategoryId,
              bankCountry: currentDefaultPurposeCodeSetting.bankCountry,
              currenciesIds: currentDefaultPurposeCodeSetting.currenciesIds,
              required: currentDefaultPurposeCodeSetting.required,
            },
          )
        ) {
          purposeCodeSettingsForUpdate.push(item);
        }
      }
    });

    if (purposeCodeSettingsForCreate?.length) {
      updateIsAvailable = false;
      const nextCreationErrors: { id: string; error: ResponseError }[] = [];

      const responses = await Promise.all(
        purposeCodeSettingsForCreate?.map(async (item) => {
          const response = await createPurposeCodeSettings({
            purposeCodeType: item.purposeCodeType,
            paymentCategoryId: item.paymentCategoryId,
            bankCountry: item.bankCountry,
            currenciesIds: item.currenciesIds,
            required: item.required,
          });

          if (response) {
            if (!response.error) {
              nextPurposeCodeSettings = nextPurposeCodeSettings.map(
                (purposeCode) => {
                  let result = { ...purposeCode };

                  if (item.id === purposeCode.id) {
                    result = response?.data?.data;
                  }

                  return result;
                },
              );
            } else if (response.error) {
              nextPurposeCodeSettings?.forEach((purposeCode) => {
                if (item.id === purposeCode.id) {
                  nextCreationErrors.push({
                    id: purposeCode.id,
                    error: response.error as ResponseError,
                  });
                }
              });
            }
          }

          return response;
        }),
      );

      if (responses) {
        setPurposeCodesSettings(nextPurposeCodeSettings);
        updateIsAvailable = true;
      }

      if (nextCreationErrors?.length) {
        setCreationErrors(nextCreationErrors);
      }
    }

    if (purposeCodeSettingsForUpdate?.length && updateIsAvailable) {
      const nextUpdateErrors: { id: string; error: ResponseError }[] = [];

      const responses = await Promise.all(
        purposeCodeSettingsForUpdate?.map(async (item) => {
          const response = await updatePurposeCodeSettings({
            id: item.id,
            purposeCodeType: item.purposeCodeType,
            paymentCategoryId: item.paymentCategoryId,
            bankCountry: item.bankCountry,
            currenciesIds: item.currenciesIds,
            required: item.required,
          });

          if (response) {
            if (!response.error) {
              nextPurposeCodeSettings = nextPurposeCodeSettings.map(
                (purposeCode) => {
                  let result = { ...purposeCode };

                  if (item.id === purposeCode.id) {
                    result = response?.data?.data;
                  }

                  return result;
                },
              );
            } else if (response.error) {
              nextPurposeCodeSettings?.forEach((purposeCode) => {
                if (item.id === purposeCode.id) {
                  nextUpdateErrors.push({
                    id: purposeCode.id,
                    error: response.error as ResponseError,
                  });
                }
              });
            }
          }

          return response;
        }),
      );

      if (responses) {
        setPurposeCodesSettings(nextPurposeCodeSettings);
      }

      if (nextUpdateErrors?.length) {
        setUpdateErrors(nextUpdateErrors);
      }
    }
  };

  useEffect(() => {
    if (deleteIsSuccess) {
      setPurposeCodesSettings(
        purposeCodesSettings?.filter(
          (item) => item.id !== selectedPurposeCodeSettingId,
        ),
      );

      setSelectedPurposeCodeSettingId('');
    }
  }, [deleteIsSuccess]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.PURPOSE_CODE_SETTINGS_LIST)
    ) {
      getPurposeCodesSettings();
    }
  }, [activePermissions]);

  useEffect(() => {
    if (
      purposeCodesSettingsList &&
      purposeCodesSettingsList?.length &&
      purposeCodesSettings?.length === 1 &&
      purposeCodesSettings[0].id.includes('purposeCode')
    ) {
      setPurposeCodesSettings(purposeCodesSettingsList);
    }
  }, [purposeCodesSettingsList, purposeCodesSettings]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.PURPOSE_CODE_SETTINGS_LIST)) {
    content = <PermissionDenied />;
  } else if (
    activePermissions?.includes(PermissionEnum.PURPOSE_CODE_SETTINGS_LIST)
  ) {
    if (isLoading || isFetching) {
      content = (
        <div className={cn.purposeCodesLoader}>
          <IconLoader />
        </div>
      );
    } else {
      content = (
        <div className={cn.paymentTypesWrapper}>
          <div
            className={`${cn.paymentTypesTitle} ${cn.paymentTypesTitleFlex}`}
          >
            Purpose code rule settings
            {activePermissions.includes(
              PermissionEnum.PURPOSE_CODE_SETTINGS_CREATE_ACTION,
            ) && (
              <ButtonSecondary
                text="Add rule"
                icon={<IconExpand />}
                size={ButtonSize.SMALL}
                className={`button-white ${cn.addPurposeCodeRuleButton}`}
                onClick={() => {
                  const nextPurposeCodeSettings = (
                    [] as PurposeCodeSettings[]
                  ).concat(purposeCodesSettings);

                  nextPurposeCodeSettings.push({
                    id: `purposeCode-${nextPurposeCodeSettings.length + 1}`,
                    purposeCodeType: '',
                    paymentCategoryId: '',
                    bankCountry: '',
                    currenciesIds: [],
                    required: false,
                  });

                  setPurposeCodesSettings(nextPurposeCodeSettings);

                  const settingsWrapper = document.getElementById('settings');

                  if (settingsWrapper) {
                    setTimeout(() => {
                      settingsWrapper.scrollTo(
                        0,
                        settingsWrapper.scrollHeight + 460,
                      );
                    }, 100);
                  }
                }}
              />
            )}
          </div>
          <div className={cn.purposeCodesWrapper}>
            {purposeCodesSettings?.map((item, index) => (
              <PurposeCodeItem
                key={item.id}
                purposeCodeItem={item}
                index={index}
                purposeCodesSettings={purposeCodesSettings}
                setPurposeCodesSettings={setPurposeCodesSettings}
                countries={countriesList}
                onDeletePurposeCodeSettings={onDeletePurposeCodeSettings}
                paymentTypes={paymentTypes}
                deleteInProgress={deleteInProgress}
                selectedPurposeCodeSettingId={selectedPurposeCodeSettingId}
                deleteIsAvailable={Boolean(
                  item.id?.includes('purposeCode') ||
                  (!item.id.includes('purposeCode') &&
                    activePermissions.includes(
                      PermissionEnum.PURPOSE_CODE_SETTINGS_DELETE_ACTION,
                    )),
                )}
                creationErrors={creationErrors?.find(
                  (error) => item.id === error.id,
                )}
                creationErrorsList={creationErrors}
                setCreationErrors={setCreationErrors}
                updateErrors={updateErrors?.find(
                  (error) => item.id === error.id,
                )}
                updateErrorsList={updateErrors}
                setUpdateErrors={setUpdateErrors}
              />
            ))}
          </div>
          {purposeCodesSettings?.length > 0 && (
            <div className={cn.saveButtonWrapper}>
              <ButtonPrimary
                text={
                  createInProgress || updateInProgress ? 'Loading...' : 'Save'
                }
                disabled={createInProgress || updateInProgress}
                onClick={onSubmit}
              />
            </div>
          )}
        </div>
      );
    }
  }

  return content;
};

export default PurposeCodes;
